/**
 * SNotes AI Bot(챗봇) ↔ 화면 명령 핸들러 브리지.
 *
 * 챗봇은 전역 컴포넌트고 메뉴 화면은 require.context로 런타임 로딩되므로
 * props로 이어줄 경로가 없다. 화면이 마운트되며 자기 핸들러를 여기에 등록하면,
 * 챗봇은 "지금 활성 탭에 등록된 핸들러가 있는가"만 물어보고 명령을 넘긴다.
 *
 * 챗봇은 어떤 화면이 등록했는지 알 필요가 없다 — 특정 메뉴 모듈을 직접
 * import하지 않게 해서 의존 방향을 (화면 → shared) 한쪽으로 유지한다.
 */
import { globalMenu } from "./Global";

export interface ChatCommandHandler {
  /** 이 핸들러를 등록한 화면의 cd_menu — 해당 탭이 활성일 때만 명령을 받는다 */
  cdMenu: string;
  /** 챗봇 입력 처리 → 말풍선에 그대로 표시할 텍스트 반환 (실패도 텍스트로) */
  handle: (command: string) => Promise<string>;
}

/**
 * 등록된 핸들러 스택 — 마지막에 등록된 것이 활성이다.
 *
 * 단일 슬롯으로 두면 화면 A 등록 → 화면 B 등록 → B 해제 순서일 때
 * 아직 살아 있는 A가 조용히 사라진다(B 해제가 슬롯을 비워버림).
 * 스택으로 두면 B만 빠지고 A가 다시 활성이 된다.
 */
const handlers: ChatCommandHandler[] = [];

function activeHandler(): ChatCommandHandler | null {
  return handlers.length ? handlers[handlers.length - 1] : null;
}

export function registerChatCommandHandler(h: ChatCommandHandler) {
  const i = handlers.indexOf(h);
  if (i !== -1) handlers.splice(i, 1); // 중복 등록 시 맨 위로 올린다
  handlers.push(h);
}

export function unregisterChatCommandHandler(h: ChatCommandHandler) {
  const i = handlers.indexOf(h);
  if (i !== -1) handlers.splice(i, 1);
}

/** 챗봇 입력을 화면 명령으로 보낼 상황인가 (핸들러 등록 + 그 화면이 활성 탭) */
export function isChatCommandTarget(): boolean {
  const h = activeHandler();
  return h != null && globalMenu.getState().activeMenuInfo?.cd_menu === h.cdMenu;
}

/** 챗봇에서 온 명령을 등록된 핸들러로 전달 */
export async function sendChatCommand(command: string): Promise<string> {
  const h = activeHandler();
  if (!h) return "명령을 받을 화면이 열려 있지 않습니다.";
  return h.handle(command);
}
